// DevToolbar — dev-only floating role switcher | sidebar-bottom on marketplace, fixed bottom-right elsewhere | switches student/counselor without Supabase changes

import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { mkt } from '../styles/marketplaceTokens'

if (!import.meta.env.DEV) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__devToolbarLoaded = false
}

// Dev credentials come from client/.env.local (gitignored) so no account
// details are committed. See client/.env.example for the variable names.
// The student pair falls back to the shared throwaway test account; the
// counselor pair has no fallback because it may be a personal account.
const DEV_STUDENT_EMAIL = import.meta.env.VITE_DEV_STUDENT_EMAIL ?? 'student@triptic.ai'
const DEV_STUDENT_PASSWORD = import.meta.env.VITE_DEV_STUDENT_PASSWORD ?? 'testpassword123'
const DEV_COUNSELOR_EMAIL = import.meta.env.VITE_DEV_COUNSELOR_EMAIL
const DEV_COUNSELOR_PASSWORD = import.meta.env.VITE_DEV_COUNSELOR_PASSWORD

function DevToolbar() {
  if (!import.meta.env.DEV) return null
  return <DevToolbarFixed />
}

// Fixed bottom-right version — used by App.tsx on all non-marketplace routes
function DevToolbarFixed() {
  const location = useLocation()
  // On marketplace the bar lives inside the sidebar instead
  if (location.pathname.startsWith('/marketplace')) return null
  return (
    <div
      style={{
        position: 'fixed',
        bottom: '16px',
        right: '16px',
        zIndex: 9999,
        background: mkt.bg2,
        border: `1px solid ${mkt.border}`,
        borderRadius: '8px',
        padding: '5px 10px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
        fontFamily: mkt.fontSans,
      }}
    >
      <DevToolbarButtons />
    </div>
  )
}

// Inline version — rendered inside the sidebar on the marketplace
export function DevToolbarInline() {
  if (!import.meta.env.DEV) return null
  return (
    <div
      style={{
        borderTop: '1px solid rgba(255,255,255,0.07)',
        padding: '12px 12px',
        flexShrink: 0,
        background: mkt.bg2,
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <DevToolbarButtons />
    </div>
  )
}

// Shared button row — no wrapper positioning
function DevToolbarButtons() {
  const [switching, setSwitching] = useState(false)
  const [activeRole, setActiveRole] = useState<'student' | 'counselor' | null>(null)

  function handleLogout() {
    supabase.auth.signOut()
    window.location.replace('/')
  }

  const buttonBase: React.CSSProperties = {
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 500,
    fontFamily: mkt.fontSans,
    border: 'none',
    cursor: switching ? 'not-allowed' : 'pointer',
    transition: 'background 120ms, color 120ms',
    lineHeight: '1.4',
    background: 'rgba(255,255,255,0.08)',
    color: '#ffffff',
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', width: '100%', overflow: 'hidden' }}>
      <span style={{ fontSize: '10px', fontWeight: 600, color: mkt.text3, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        DEV
      </span>
      <div style={{ width: '1px', height: '14px', background: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />
      <button
        disabled={switching}
        style={{ ...buttonBase, ...(activeRole === 'student' ? { background: '#6c52e8', color: '#fff' } : {}) }}
        onClick={async () => {
          setActiveRole('student')
          setSwitching(true)
          // Don't await signOut — it can run in background
          supabase.auth.signOut()
          await supabase.auth.signInWithPassword({
            email: DEV_STUDENT_EMAIL,
            password: DEV_STUDENT_PASSWORD,
          })
          // Workspace portal, not the marketplace (intentionally disconnected)
          window.location.href = '/student'
        }}
      >
        Student
      </button>
      <button
        disabled={switching}
        style={{ ...buttonBase, ...(activeRole === 'counselor' ? { background: '#6c52e8', color: '#fff' } : {}) }}
        onClick={async () => {
          if (!DEV_COUNSELOR_EMAIL || !DEV_COUNSELOR_PASSWORD) {
            alert(
              'Counselor dev login is not configured.\n\n' +
              'Add VITE_DEV_COUNSELOR_EMAIL and VITE_DEV_COUNSELOR_PASSWORD to ' +
              'client/.env.local, then restart the dev server.'
            )
            return
          }
          setActiveRole('counselor')
          setSwitching(true)
          // Don't await signOut — it can run in background
          supabase.auth.signOut()
          // Was test@triptic.ai, which is the STUDENT account — that is why this
          // button used to land in the student workspace with no counselor data.
          await supabase.auth.signInWithPassword({
            email: DEV_COUNSELOR_EMAIL,
            password: DEV_COUNSELOR_PASSWORD,
          })
          // '/counselor' is the workspace portal. '/counselor/dashboard' is
          // MarketplaceDashboard — deliberately not used here.
          window.location.href = '/counselor'
        }}
      >
        Counselor
      </button>
      <div style={{ width: '1px', height: '14px', background: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />
      <button onClick={handleLogout} style={{ ...buttonBase, color: '#f87171', background: 'transparent' }}>
        Log out
      </button>
    </div>
  )
}

export default DevToolbar
